import axios from 'axios';

import Session from 'shared_modules/Session';

export interface CaptureLineItemData {
  lineId: string;
  amount: number;
}

export interface CapturePaymentRequest {
  paymentId: string;
  totalAmount: number;
  saleId: string;
  lineItems?: CaptureLineItemData[];
}

export interface PaymentTransaction {
  paymentTransactionID: string;
  gateway: string;
  gatewayTransactionType: string;
  status: string;
  gatewayResponseCode?: string;
  message?: string;
  [key: string]: any;
}

export interface PaymentData {
  paymentID: string;
  status: string;
  totalAmount: string | number;
  saleID: string | number;
  shopID?: string | number;
  registerID?: string | number;
  method?: string;
  cardPaymentRequest?: any;
  Transactions?: {
    PaymentTransaction: PaymentTransaction | PaymentTransaction[];
  };
  [key: string]: any;
}

export interface CapturePaymentResponse {
  Payment: PaymentData;
}

/**
 * Fetches the full payment data from the API
 *
 * @param paymentId - The payment ID
 * @returns Promise with the payment data
 */
export const fetchPaymentData = async (paymentId: string): Promise<PaymentData> => {
  const accountId = Session.accountID();

  const response = await axios.get<{ Payment: PaymentData }>(
    `/API/V3/Account/${accountId}/Payment/${paymentId}.json`
  );

  return response.data.Payment;
};

/**
 * Captures a pre-authorized payment with line item tracking
 * Uses the specialized CaptureWithLineItems endpoint
 *
 * @param request - The capture payment request containing payment details and line items
 * @returns Promise with the captured payment response
 */
export const capturePayment = async (
  request: CapturePaymentRequest
): Promise<CapturePaymentResponse> => {
  const accountId = Session.accountID();

  // First, fetch the full payment data to get registerID, shopID, etc.
  const paymentData = await fetchPaymentData(request.paymentId);

  // Build the capture payload with payment and lineItems at same level
  const capturePayload = {
    payment: {
      ...paymentData,
      totalAmount: request.totalAmount.toFixed(2),
      saleID: request.saleId,
      shopID: paymentData.shopID,
      registerID: paymentData.registerID,
    },
    lineItems: {
      lineItems:
        request.lineItems?.map((item) => ({
          lineId: item.lineId,
          capturedAmount: item.amount,
        })) || [],
    },
  };

  // Use specialized endpoint for preauth captures
  const endpoint = `/API/V3/Account/${accountId}/Payment/${request.paymentId}/CaptureWithLineItems.json`;

  // Send the payload directly (NOT wrapped in { Payment: ... })
  const response = await axios.post<CapturePaymentResponse>(endpoint, capturePayload);

  return response.data;
};

/**
 * Completes the current sale after successful payment capture
 * Uses the register completion workflow to ensure proper business logic execution
 * (inventory fulfillment, gift cards, workorders, etc.)
 *
 * This directly calls the backend form to avoid using cached/stale data
 *
 * @returns Promise with the completed sale response
 */
export const completeSale = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (!window.doServerCall) {
      reject(new Error('doServerCall not available'));
      return;
    }

    // Call the backend form directly which will load fresh data from DB
    const contentMap = {
      method: 'POST',
      form_name: 'register.complete_transaction',
    } as any;

    window.showNotifier?.('working');

    window.doServerCall(contentMap, (result: any) => {
      window.hideNotifier?.('working');

      if (result.complete_failed) {
        // Sale completion failed
        if (result.html) {
          window.merchantos?.display?.replaceNonAjax(result.html);
        }
        reject(new Error(result.error_message || 'Sale completion failed'));
      } else if (result.html) {
        // Success - display the receipt
        window.merchantos?.display?.replaceNonAjax(result.html);

        // Handle auto-print functionality (replicate logic from completeSaleResult)
        const openCashDrawer =
          typeof result.openCashDrawer === 'boolean' ? result.openCashDrawer : true;

        if (result.auto_print) {
          if (result.receiptPreference === 'printed') {
            // Print physical receipt
            window.merchantos?.print?.printUrl({
              url: result.auto_print,
              pageType: 'receipt',
              openCashDrawer,
            });
          } else {
            // Email receipt - open in same window without print dialog
            const noPopup = result.auto_print.replace(/print=1/g, 'print=0');
            window.merchantos?.print?.printUrl({
              url: noPopup,
              target: '_self',
              pageType: 'receipt',
              openCashDrawer,
            });
          }
        } else if (openCashDrawer) {
          // No receipt to print, but still open cash drawer if needed
          window.merchantos?.print?.openCashDrawer();
        }

        resolve(result);
      } else {
        resolve(result);
      }
    });
  });
};
